import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Tooltip,
    Typography,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Download, ExpandMore, ExpandLess, CheckCircle, Error, Person, CalendarToday, Info } from '@mui/icons-material';
import { format } from 'date-fns';
import { gql, useQuery } from '@apollo/client';

const GET_COMPANY_IMPORT_HISTORY = gql`
    query GetCompanyImportHistory($eventId: String!, $role: String!) {
        companyImportHistory(eventId: $eventId, role: $role)
    }
`;

export default function CompanyImportHistory({ eventId, role }) {
    const [expandedItems, setExpandedItems] = useState({});
    const { loading, error, data } = useQuery(GET_COMPANY_IMPORT_HISTORY, {
        variables: { eventId, role },
    });

    let history = [];
    try {
        if (data?.companyImportHistory) {
            const parsed =
                typeof data.companyImportHistory === 'string'
                    ? JSON.parse(data.companyImportHistory)
                    : data.companyImportHistory;
            history = Array.isArray(parsed) ? parsed : [];
        }
    } catch (e) {
        console.error('Error parsing import history:', e);
        history = [];
    }

    const toggleExpand = id => {
        setExpandedItems(prev => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const getUserFriendlyError = reason => {
        const errorMap = {
            'Missing required field: Email': 'Email address is required',
            'Missing required field: Company': 'Company name is required',
            'Missing required field: allowedRegistrations': 'Allowed registrations is required',
            'Invalid email format': 'Please provide a valid email address',
            'Duplicate email': 'This email already exists. Please use a unique email',
            'Bad characters detected': 'Invalid characters found in data. Please check and remove special characters',
            'Unknown error occurred': 'An unexpected error occurred. Please try again or contact support',
        };
        return errorMap[reason] || reason || 'An unexpected error occurred';
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant='body2' sx={{ mt: 2 }}>
                    Loading import history...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity='error' sx={{ m: 2 }}>
                Error loading import history: {error.message}
            </Alert>
        );
    }

    if (history.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Info sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant='h6' color='text.secondary'>
                    No import history found
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                    Import history will appear here after you import {role.toLowerCase()}.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant='h6' gutterBottom>
                Import History for {role}
            </Typography>
            <Stack spacing={2}>
                {history.map((entry, index) => {
                    const isExpanded = expandedItems[entry.id];
                    const importedData = entry.imported_data || [];
                    const failedData = entry.failed_data || [];

                    return (
                        <Card key={entry.id || index} variant='outlined'>
                            <CardContent>
                                <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 1 }}>
                                            <Person color='primary' />
                                            <Typography variant='subtitle1' fontWeight={600}>
                                                {entry.uploaded_by || 'Unknown User'}
                                            </Typography>
                                            <Chip
                                                label={entry.uploaded_by_email || 'No email'}
                                                size='small'
                                                variant='outlined'
                                            />
                                        </Stack>
                                        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                                            <CalendarToday fontSize='small' color='action' />
                                            <Typography variant='body2' color='text.secondary'>
                                                {entry.created
                                                    ? format(new Date(entry.created), 'PPpp')
                                                    : 'Unknown date'}
                                            </Typography>
                                        </Stack>
                                        <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                                            <Chip
                                                icon={<Info />}
                                                label={`Total: ${entry.total_rows || 0}`}
                                                color='info'
                                                variant='outlined'
                                            />
                                            <Chip
                                                icon={<CheckCircle />}
                                                label={`Success: ${entry.successful_rows || 0}`}
                                                color='success'
                                                variant='outlined'
                                            />
                                            <Chip
                                                icon={<Error />}
                                                label={`Failed: ${entry.failed_rows || 0}`}
                                                color='error'
                                                variant='outlined'
                                            />
                                            {entry.duplicates > 0 && (
                                                <Chip
                                                    label={`Duplicates: ${entry.duplicates || 0}`}
                                                    color='warning'
                                                    variant='outlined'
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                    <IconButton onClick={() => toggleExpand(entry.id)}>
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                </Stack>

                                <Collapse in={isExpanded}>
                                    <Divider sx={{ my: 2 }} />
                                    {failedData.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant='subtitle2' gutterBottom color='error'>
                                                Failed Rows ({failedData.length})
                                            </Typography>
                                            <TableContainer component={Paper} variant='outlined'>
                                                <Table size='small'>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Row</TableCell>
                                                            <TableCell>Email</TableCell>
                                                            <TableCell>Company</TableCell>
                                                            <TableCell>Error</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {failedData.slice(0, 10).map((row, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>{row.rowNumber || idx + 1}</TableCell>
                                                                <TableCell>{row.Email || row.email || 'N/A'}</TableCell>
                                                                <TableCell>{row.Company || row.company || 'N/A'}</TableCell>
                                                                <TableCell>
                                                                    <Typography variant='caption' color='error'>
                                                                        {getUserFriendlyError(row.reason || row.error)}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                                {failedData.length > 10 && (
                                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                                        <Typography variant='caption' color='text.secondary'>
                                                            ... and {failedData.length - 10} more failed rows
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </TableContainer>
                                        </Box>
                                    )}
                                    {importedData.length > 0 && (
                                        <Box>
                                            <Typography variant='subtitle2' gutterBottom color='success.main'>
                                                Successful Rows ({importedData.length})
                                            </Typography>
                                            <Typography variant='body2' color='text.secondary'>
                                                {importedData.length} {role.toLowerCase()} were successfully imported.
                                            </Typography>
                                        </Box>
                                    )}
                                </Collapse>
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>
        </Box>
    );
}

